import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import {
  productService,
  storeService,
  warehouseService,
  categoryService,
  employeeService,
  userApiService,
  companyService,
  salesOrderService,
  purchaseOrderService,
  invoiceService,
  attributeService,
  attributeValueService,
  roleService,
  menuService,
  subscriptionPlanService,
  tenantService,
  customerService,
  supplierService,
  currencyService,
  taxService,
  brandService,
  unitService,
  accountService,
} from '@kss/api';

/**
 * Source services we know how to resolve. Add more as needed — every
 * key here becomes a valid `kind` to pass to `useNameMap`.
 */
const SOURCES = {
  product: { service: productService, idKey: 'productId', nameKey: 'name' },
  store: { service: storeService, idKey: 'storeId', nameKey: 'name' },
  warehouse: { service: warehouseService, idKey: 'warehouseId', nameKey: 'name' },
  category: { service: categoryService, idKey: 'categoryId', nameKey: 'name' },
  employee: {
    service: employeeService,
    idKey: 'employeeId',
    // Employees join firstName + lastName; nameKey is special-cased below.
    nameKey: '__employeeFullName',
  },
  user: { service: userApiService, idKey: 'userId', nameKey: 'userName' },
  company: { service: companyService, idKey: 'companyId', nameKey: 'name' },
  salesOrder: {
    service: salesOrderService,
    idKey: 'salesOrderId',
    nameKey: 'orderNumber',
  },
  purchaseOrder: {
    service: purchaseOrderService,
    idKey: 'purchaseOrderId',
    nameKey: 'poNumber',
  },
  invoice: { service: invoiceService, idKey: 'invoiceId', nameKey: 'invoiceNumber' },
  attribute: { service: attributeService, idKey: 'attributeId', nameKey: 'name' },
  attributeValue: { service: attributeValueService, idKey: 'attributeValueId', nameKey: 'value' },
  role: { service: roleService, idKey: 'roleId', nameKey: 'name' },
  menu: { service: menuService, idKey: 'menuId', nameKey: 'name' },
  subscriptionPlan: { service: subscriptionPlanService, idKey: 'subscriptionPlanId', nameKey: 'name' },
  tenant: { service: tenantService, idKey: 'tenantId', nameKey: 'name' },
  // Added 2026-05-02 — live API now exposes these controllers.
  customer: { service: customerService, idKey: 'customerId', nameKey: 'name' },
  supplier: { service: supplierService, idKey: 'supplierId', nameKey: 'name' },
  currency: { service: currencyService, idKey: 'currencyId', nameKey: 'code' },
  tax: { service: taxService, idKey: 'taxId', nameKey: 'name' },
  brand: { service: brandService, idKey: 'brandId', nameKey: 'name' },
  unit: { service: unitService, idKey: 'unitId', nameKey: 'name' },
  // Chart-of-accounts row (used by Expense.categoryAccountId,
  // BankTransaction.bankAccountId, JournalEntry lines, etc.)
  account: { service: accountService, idKey: 'accountId', nameKey: 'name' },
} as const;

export type NameMapKind = keyof typeof SOURCES;

function readDisplayName(row: Record<string, unknown>, nameKey: string): string {
  if (nameKey === '__employeeFullName') {
    const first = String(row.firstName ?? '').trim();
    const last = String(row.lastName ?? '').trim();
    const full = [first, last].filter(Boolean).join(' ');
    return full || String(row.employeeCode ?? '');
  }
  const v = row[nameKey];
  return v == null ? '' : String(v);
}

export interface NameMap {
  /** id → display name. Misses fall back to the empty string. */
  byId: Record<string, string>;
  /**
   * id → display name with a clean fallback. When the id is not in the
   * map (still loading, deleted entity, etc.) returns the truncated id
   * rather than an empty cell.
   */
  resolve: (id: string | null | undefined) => string;
  loading: boolean;
}

/**
 * Loads one entity's `all()` once and exposes id→name lookups. Designed
 * to back DataGrid columns that today render raw UUIDs (`productId`,
 * `storeId`, `warehouseId`, etc.).
 *
 * Usage:
 *   const products = useNameMap('product');
 *   render: (r) => products.resolve(r.productId)
 */
// Module-level cache so N components calling `useNameMap('product')`
// share a single fetch + a single map. Without this, every `<NameCell>`
// in a 100-row grid would each fire its own `productService.all()`.
const _cache: Partial<Record<NameMapKind, Promise<Record<string, string>>>> = {};
const _resolved: Partial<Record<NameMapKind, Record<string, string>>> = {};

function fetchMap(kind: NameMapKind): Promise<Record<string, string>> {
  const existing = _cache[kind];
  if (existing) return existing;
  const cfg = SOURCES[kind];
  const p = (async () => {
    try {
      const data = await cfg.service.all();
      const next: Record<string, string> = {};
      for (const row of (Array.isArray(data) ? data : []) as Record<string, unknown>[]) {
        const id = row[cfg.idKey];
        if (typeof id !== 'string' || !id) continue;
        next[id] = readDisplayName(row, cfg.nameKey);
      }
      _resolved[kind] = next;
      return next;
    } catch {
      _resolved[kind] = {};
      return {};
    }
  })();
  _cache[kind] = p;
  return p;
}

/** Drop the cached promise for a kind so the next mount re-fetches. */
export function invalidateNameMap(kind: NameMapKind): void {
  delete _cache[kind];
  delete _resolved[kind];
}

export function useNameMap(kind: NameMapKind): NameMap {
  const cached = _resolved[kind];
  const [byId, setById] = useState<Record<string, string>>(cached ?? {});
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    setLoading(true);
    fetchMap(kind).then((next) => {
      if (!cancelled) {
        setById(next);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [kind, cached]);

  return useMemo<NameMap>(
    () => ({
      byId,
      loading,
      resolve: (id) => {
        if (!id) return '—';
        const hit = byId[String(id)];
        if (hit) return hit;
        /* While the name map is still loading, show an ellipsis so
           the row doesn't flash a confusing GUID. Once loading is
           done and the id still didn't resolve (deleted entity,
           cross-tenant orphan, or the entire service call failed —
           see backend D1), show "—" instead of a truncated GUID
           prefix. The previous "abc12345…" fallback looked like a
           numeric ID to users. Bug ref: AI Agent 2026-06-11 —
           "In the Customer column, numeric IDs are displayed
           instead of customer names". */
        return loading ? '…' : '—';
      },
    }),
    [byId, loading],
  );
}

/**
 * Drop-in cell renderer for DataGrid columns that today render raw
 * UUIDs. Uses the cached `useNameMap` so a 100-row grid still fires
 * exactly one fetch per `kind`.
 *
 *   { accessor: 'productId',
 *     render: (r) => <NameCell kind="product" id={r.productId} /> }
 *
 * `fallback` — rendered (in muted color) when the id is well-formed
 * but not present in the loaded map. Common causes: a deleted parent
 * row, a cross-tenant orphan, or (SSO ISSUES 2 & 3) a guest-checkout
 * `SalesOrder.customerId` that actually holds a `User.id` and never
 * had a matching `/api/Customer/all` row to begin with. Pages that
 * know a friendlier label (e.g. "Guest customer") pass it here so
 * grids never render a blank cell. Omitted → renders `—`.
 */
export function NameCell({
  kind,
  id,
  fallback,
}: {
  kind: NameMapKind;
  id: string | null | undefined;
  fallback?: ReactNode;
}): ReactElement {
  const map = useNameMap(kind);
  if (!id) return <span className="text-sm text-content-muted">—</span>;
  const hit = map.byId[String(id)];
  if (hit) return <span className="text-sm">{hit}</span>;
  if (map.loading) {
    // While the map is still fetching we can't tell hit vs miss —
    // render a subtle placeholder so the row doesn't flash a stale
    // fallback that then swaps to the real name a beat later.
    return <span className="text-xs italic text-content-muted">Loading…</span>;
  }
  if (fallback !== undefined && fallback !== null && fallback !== '') {
    return <span className="text-sm text-content-muted">{fallback}</span>;
  }
  return <span className="text-sm text-content-muted">—</span>;
}

/**
 * Convenience wrapper — load several name maps in one call. The hook
 * runs each `useNameMap` independently so they fetch in parallel.
 *
 *   const { product, store, warehouse } = useNameMaps('product','store','warehouse');
 */
export function useNameMaps<K extends NameMapKind>(...kinds: K[]): Record<K, NameMap> {
  // We deliberately don't loop with Array.from — React rules-of-hooks
  // forbid hooks in loops. Up to 6 maps is enough for every page in the
  // repo today; expand if a list page needs more.
  const a = useNameMap(kinds[0] ?? ('product' as K));
  const b = useNameMap(kinds[1] ?? ('product' as K));
  const c = useNameMap(kinds[2] ?? ('product' as K));
  const d = useNameMap(kinds[3] ?? ('product' as K));
  const e = useNameMap(kinds[4] ?? ('product' as K));
  const f = useNameMap(kinds[5] ?? ('product' as K));
  const all = [a, b, c, d, e, f];
  const out = {} as Record<K, NameMap>;
  kinds.forEach((k, i) => {
    out[k] = all[i]!;
  });
  return out;
}
